import React, { useMemo, useRef, useEffect } from 'react';
import { Marker } from 'react-map-gl/maplibre';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import {
  getActiveMarker,
  getCurrentLocationChildLocations,
  getSelectedLocationVarValues,
} from '../../../state/selectors';
import { HoverType, HoveredLoc, Marker as MarkerProps } from '../mapHelpers';
import useStyles from '../../../styles';
import DoughnutPlot from '../../Plots/DoughnutPlot';
import { setCurrentLocation } from '../../../state/actions';
import { VarName } from '../../../utils/varNames';
import MotionRingPlot from '../../Plots/MotionRingPlot';
import SensorSunburst from '../../Plots/SensorSunburst';
import { DataTreeItem, LocationType } from '../../../services/api';
import { useAppDispatch, useAppSelector } from '../../../state/store';
import { themeProps } from '../../../styles/theme';

interface ItemRefs {
  [key: string]: HTMLElement | null;
}

interface CustomLocMarkerProps {
  hoveredLoc: HoveredLoc | undefined;
  setHoveredLoc: (value: HoveredLoc | undefined) => void;
}

// display floor level summary with pie chart
function CustomLocationMarker({ hoveredLoc, setHoveredLoc }: CustomLocMarkerProps): JSX.Element {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const listRef = useRef(null);
  const itemRefs = useRef<ItemRefs>({});
  const activeMarker = useAppSelector(getActiveMarker);
  const childLocations = useAppSelector(getCurrentLocationChildLocations);
  const selectedVarValues = useAppSelector(getSelectedLocationVarValues);

  const hasFloorLevel = useMemo(() => {
    let floorExists = false;
    const floorLevel = childLocations.filter((item) => item.type === LocationType.FloorLevel);
    if (floorLevel.length > 0) {
      floorExists = true;
    }
    return floorExists;
  }, [childLocations]);

  // Scroll to selected item whenever selection changes
  useEffect(() => {
    const hoveredLocId = hoveredLoc?.id;
    const isMapHovered = hoveredLoc?.hoveredType === HoverType.map;
    if (hoveredLocId && isMapHovered && itemRefs.current[hoveredLocId]) {
      itemRefs.current[hoveredLocId]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [hoveredLoc]);

  const newLocationMarkers = useMemo(() => {
    const locMarkers = new Map<string, MarkerProps>();
    if (hasFloorLevel) {
      const floorData = childLocations.sort((a, b) => {
        const floorA = a?.shortName ?? a?.name ?? '';
        const floorB = b?.shortName ?? b?.name ?? '';
        if (floorA < floorB) return 1;
        if (floorA > floorB) return -1;
        return 0;
      });

      floorData.forEach((location) => {
        const { id } = location;
        if (id) {
          const locationMarkerData: MarkerProps = {
            name: location.name ?? location.id,
            id: location.id,
            location: location.id,
            coordinates: [location.position?.lng, location.position?.lat],
            availableData: [],
          };
          locMarkers.set(id, locationMarkerData as MarkerProps);
        }
      });
    } else {
      childLocations?.forEach((location) => {
        const { id } = location;
        if (id) {
          const locationMarkerData: MarkerProps = {
            name: location.name ?? location.id,
            id: location.id,
            location: location.id,
            coordinates: [location.position?.lng, location.position?.lat],
            availableData: [],
          };
          locMarkers.set(id, locationMarkerData as MarkerProps);
        }
      });
    }
    return locMarkers;
  }, [childLocations, hasFloorLevel]);

  const showLocRingContent = (locProps: MarkerProps) => {
    function showRingPlots() {
      switch (activeMarker) {
        case VarName.ClientsBle:
        case VarName.ClientsWiFi:
        case VarName.EnergyInkWh: {
          const allData: DataTreeItem[] = selectedVarValues.get(activeMarker) ?? [];
          const locData = allData.filter(
            (item) => item.location === locProps.id || item.location?.startsWith(`${locProps.id}#`)
          );
          return <SensorSunburst data={locData} varName={activeMarker} locMarkerId={locProps.id} />;
        }
        case VarName.MotionEvent:
          return <MotionRingPlot locMarkerId={locProps.id} />;
        default:
          return <DoughnutPlot varName={activeMarker} locMarkerId={locProps.id} />;
      }
    }

    return (
      <Box
        className={classes.locationMarker}
        sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}
      >
        {showRingPlots()} &nbsp; <Typography variant="body1">{locProps.name}</Typography>
      </Box>
    );
  };

  // when child locations are buildings, show ring summary of each building location on their coordinates
  const showBuildingContent = () => (
    <>
      {Array.from(newLocationMarkers.values()).map((locationProps: MarkerProps) => {
        const lat = locationProps.coordinates[0];
        const lng = locationProps.coordinates[1];

        return (
          <Box key={locationProps.id}>
            {lat !== undefined && lng !== undefined && (
              <Marker
                longitude={lat}
                latitude={lng}
                anchor="center"
                style={{ zIndex: 5 }}
                onClick={() => dispatch(setCurrentLocation(locationProps.id))}
              >
                {showLocRingContent(locationProps)}
              </Marker>
            )}
          </Box>
        );
      })}
    </>
  );

  // when child locations are floors, show summary of all floors with ring summary in fixed position
  const showFloorContent = () => {
    const allFloors = Array.from(newLocationMarkers.values());

    return (
      <Box
        sx={{
          width: '200px',
          position: 'absolute',
          top: 200,
          right: 0,
          zIndex: 10,
          height: 'calc(100vh - 200px)',
          overflow: 'scroll',
        }}
      >
        <List ref={listRef}>
          {allFloors.map((floor) => {
            const hoveredId = hoveredLoc?.id;
            const isHoveredFoor = hoveredId && floor.id === hoveredId;
            const floorCode = floor.id.substring(floor.id.lastIndexOf('#') + 1);

            return (
              <ListItem
                key={floor.id}
                // eslint-disable-next-line no-return-assign
                ref={(el) => (itemRefs.current[floorCode] = el)}
                onClick={() => {
                  dispatch(setCurrentLocation(floor.id));
                  setHoveredLoc(undefined);
                }}
                sx={{
                  padding: '0 8px',
                  background: isHoveredFoor ? `${themeProps.colors.alertYellow}40` : 'none',
                  '&:hover': {
                    cursor: 'pointer',
                  },
                }}
                onMouseEnter={() => setHoveredLoc({ id: floor.id, hoveredType: HoverType.list })}
                onMouseLeave={() => setHoveredLoc(undefined)}
              >
                {showLocRingContent(floor)}
              </ListItem>
            );
          })}
        </List>
      </Box>
    );
  };

  return <Box>{hasFloorLevel ? showFloorContent() : showBuildingContent()};</Box>;
}

export default CustomLocationMarker;
